import type { GraphQLClient } from 'graphql-request'

import { isAgentKind, toPresence } from './graphql'
import type { Me, Meeting, OrgRef, Person, Pulse, TaskLite } from './types'

const ME_QUERY = /* GraphQL */ `
  query GlassOfficeMe {
    me {
      id
      name
      email
      lastOrganizationId
      organizations { id name status }
    }
  }
`

const PULSES_QUERY = /* GraphQL */ `
  query GlassOfficePulses($organizationId: String) {
    pulses(organizationId: $organizationId) {
      id
      name
      category
      type
      organization_id
      member_count
      unread_team_messages { id }
      notification_count
      latest_update
      team_thread { id topics { id name unreadCount } }
      members { last_visited user { id } }
    }
  }
`

const ORG_USERS_QUERY = /* GraphQL */ `
  query GlassOfficeOrgUsers($organizationId: String!, $first: Int, $page: Int) {
    organizationUsers(organizationId: $organizationId, first: $first, page: $page) {
      paginatorInfo { currentPage lastPage total }
      data {
        id
        role
        status
        jobTitle
        department
        user { id name kind presence lastSeenAt picture gravatar }
      }
    }
  }
`

// Pulses organize work as LIST containers whose CHILDREN are the real tasks
// (the server returns top-level rows only, children nested). Walk two levels
// so desk signs and heat reflect actual work items, not container rows.
const TASKS_QUERY = /* GraphQL */ `
  query GlassOfficeTasks($organizationId: String!, $entityId: String) {
    tasks(organizationId: $organizationId, entityId: $entityId) {
      id
      title
      status
      type
      assignees { user { id } }
      children {
        id
        title
        status
        type
        assignees { user { id } }
        children {
          id
          title
          status
          type
          assignees { user { id } }
        }
      }
    }
  }
`

interface MeResponse {
  me: {
    id: string
    name: string
    email?: string | null
    lastOrganizationId?: string | null
    organizations?: OrgRef[] | null
  }
}

export async function fetchMe(client: GraphQLClient): Promise<Me> {
  const { me } = await client.request<MeResponse>(ME_QUERY)
  return {
    id: me.id,
    name: me.name,
    email: me.email,
    lastOrganizationId: me.lastOrganizationId,
    organizations: me.organizations ?? [],
  }
}

interface PulsesResponse {
  pulses?: Array<{
    id: string
    name: string
    category: string
    type?: string | null
    organization_id: string
    member_count?: string | number | null
    unread_team_messages?: Array<{ id: string }> | null
    notification_count?: string | number | null
    latest_update?: string | null
    team_thread?: {
      id: string
      topics?: Array<{
        id?: string
        name: string
        unreadCount?: string | number | null
      }> | null
    } | null
    members?: Array<{
      last_visited?: string | null
      user?: { id: string } | null
    }> | null
  }> | null
}

export async function fetchPulses(
  client: GraphQLClient,
  organizationId: string,
): Promise<Pulse[]> {
  const data = await client.request<PulsesResponse>(PULSES_QUERY, {
    organizationId,
  })
  return (data.pulses ?? [])
    // The API also returns the caller's NETWORK pulses — cross-org connection
    // 1:1s with organization_id NULL (Nova hides them in org context the same
    // way). They're not part of this workspace: no booth on the floor, not a
    // message destination, not in the directory.
    .filter((p) => p.organization_id === organizationId)
    .map((p) => ({
    id: p.id,
    name: p.name,
    category: p.category,
    type: p.type,
    organizationId: p.organization_id,
    teamThreadId: p.team_thread?.id ?? null,
    latestUpdate: p.latest_update ?? null,
    memberUserIds: (p.members ?? [])
      .map((m) => m.user?.id)
      .filter((id): id is string => Boolean(id)),
    // Per-member last_visited (epoch ms) — the real signal for "where did this
    // person most recently work", used to seat multi-pulse people honestly.
    memberVisits: Object.fromEntries(
      (p.members ?? [])
        .filter((m) => m.user?.id)
        .map((m) => [
          m.user!.id,
          m.last_visited ? Date.parse(m.last_visited) || null : null,
        ]),
    ),
    memberCount: p.member_count != null ? Number(p.member_count) : null,
    // TRUE unread messages for the viewer (ids only, tiny payload).
    // unread_notifications was pending NOTIFICATION rows — on 1:1 pulses those
    // accumulate forever, painting every wing room "unread" (Malek, 2026-07-11).
    unreadCount: p.unread_team_messages?.length ?? 0,
    notificationCount:
      p.notification_count != null ? Number(p.notification_count) : null,
    topics: (p.team_thread?.topics ?? []).map((t) => ({
      id: t.id,
      name: t.name,
      unreadCount: t.unreadCount != null ? Number(t.unreadCount) : 0,
    })),
  }))
}

interface OrgUsersResponse {
  organizationUsers?: {
    paginatorInfo?: { currentPage: number; lastPage: number; total: number }
    data?: Array<{
      id: string
      role?: string | null
      status?: string | null
      jobTitle?: string | null
      department?: string | null
      user?: {
        id: string
        name: string
        kind?: string | null
        presence?: string | null
        lastSeenAt?: string | null
        picture?: string | null
        gravatar?: string | null
      } | null
    }> | null
  } | null
}

export async function fetchOrgUsers(
  client: GraphQLClient,
  organizationId: string,
): Promise<Person[]> {
  const people: Person[] = []
  let page = 1
  // Page through so large orgs are fully populated (runaway guard at 50 pages
  // = 5000 members; beyond that we log rather than silently truncate).
  for (let guard = 0; guard < 50; guard += 1) {
    const data = await client.request<OrgUsersResponse>(ORG_USERS_QUERY, {
      organizationId,
      first: 100,
      page,
    })
    const block = data.organizationUsers
    for (const row of block?.data ?? []) {
      if (!row.user) continue
      const isAgent = isAgentKind(row.user.kind)
      people.push({
        organizationUserId: row.id,
        userId: row.user.id,
        name: row.user.name,
        role: row.role,
        status: row.status,
        jobTitle: row.jobTitle,
        department: row.department,
        picture: row.user.picture,
        gravatar: row.user.gravatar,
        isAgent,
        // Agents are always-on workers — show them awake regardless of session presence.
        presence: isAgent ? 'online' : toPresence(row.user.presence),
        lastSeenAt: row.user.lastSeenAt,
      })
    }
    const info = block?.paginatorInfo
    if (!info || info.currentPage >= info.lastPage) break
    page += 1
  }
  return people
}

interface TasksResponse {
  tasks?: TaskLiteNode[] | null
}

interface TaskLiteNode {
  id: string
  title: string
  status?: string | null
  type?: string | null
  assignees?: Array<{ user?: { id: string } | null }> | null
  children?: TaskLiteNode[] | null
}

export async function fetchTasksForPulse(
  client: GraphQLClient,
  organizationId: string,
  pulseId: string,
): Promise<TaskLite[]> {
  const data = await client.request<TasksResponse>(TASKS_QUERY, {
    organizationId,
    entityId: pulseId,
  })
  // Flatten the list→task→subtask tree into real work items; LIST/MILESTONE
  // container rows are structure, not work — drop them from counts.
  const flat: TaskLite[] = []
  const walk = (node: TaskLiteNode) => {
    const type = (node.type ?? 'TASK').toUpperCase()
    if (type !== 'LIST' && type !== 'MILESTONE') {
      flat.push({
        id: node.id,
        title: node.title,
        status: node.status,
        type: node.type,
        pulseId,
        assigneeUserIds: (node.assignees ?? [])
          .map((a) => a.user?.id)
          .filter((id): id is string => Boolean(id)),
      })
    }
    for (const child of node.children ?? []) walk(child)
  }
  for (const row of data.tasks ?? []) walk(row)
  return flat
}

// --- Meetings -------------------------------------------------------------
// Per-pulse meeting sessions for today plus anything LIVE right now. "Live" is
// decided in data/meetings.ts using the dashboard's LiveMeetingsContext rule.
const MEETINGS_QUERY = /* GraphQL */ `
  query GlassOfficeMeetings($organizationId: String!, $onDate: Date) {
    meetingSessions(organizationId: $organizationId, onDate: $onDate) {
      id
      name
      pulseId
      status
      companion_status
      invite_pulse
      type
      start_at
      end_at
      attendees { user { id } }
      event { attendees { user { id } } }
    }
  }
`

const ACTIVE_SESSIONS_QUERY = /* GraphQL */ `
  query GlassOfficeActiveSessions($organizationId: String!) {
    meetingSessions(organizationId: $organizationId, status: ACTIVE) {
      id
      name
      pulseId
      status
      companion_status
      invite_pulse
      type
      start_at
      end_at
      attendees { user { id } }
      event { attendees { user { id } } }
    }
  }
`

type AttendeeNode = { user?: { id: string } | null }
interface SessionNode {
  id: string
  name?: string | null
  pulseId?: string | null
  status?: string | null
  companion_status?: string | null
  invite_pulse?: boolean | null
  type?: string | null
  start_at?: string | null
  end_at?: string | null
  attendees?: AttendeeNode[] | null
  event?: { attendees?: AttendeeNode[] | null } | null
}

function attendeeIds(list: AttendeeNode[] | null | undefined): string[] {
  return (list ?? [])
    .map((a) => a.user?.id)
    .filter((id): id is string => Boolean(id))
}

function toMeeting(s: SessionNode): Meeting {
  const joined = attendeeIds(s.attendees)
  // The people this meeting is FOR: the parent event's invite list. Session
  // attendee rows only appear once someone connects, so pulse-calendar
  // meetings otherwise showed an empty table with just the notetaker.
  const invited = [...new Set([...attendeeIds(s.event?.attendees), ...joined])]
  return {
    id: s.id,
    name: s.name ?? 'Meeting',
    pulseId: s.pulseId ?? null,
    startAt: s.start_at ?? null,
    endAt: s.end_at ?? null,
    attendeeUserIds: joined,
    invitedUserIds: invited,
    status: s.status ?? null,
    companionStatus: s.companion_status ?? null,
    invitePulse: Boolean(s.invite_pulse),
    type: s.type ?? null,
  }
}

export async function fetchMeetings(
  client: GraphQLClient,
  organizationId: string,
): Promise<Meeting[]> {
  const meetings: Meeting[] = []
  const seen = new Set<string>()
  const add = (rows: SessionNode[] | null | undefined): void => {
    for (const s of rows ?? []) {
      if (!s?.id || seen.has(s.id)) continue
      seen.add(s.id)
      meetings.push(toMeeting(s))
    }
  }

  // Today's sessions across the org's pulses.
  try {
    const d = new Date()
    const pad = (n: number): string => String(n).padStart(2, '0')
    const onDate = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
    const data = await client.request<{ meetingSessions?: SessionNode[] | null }>(
      MEETINGS_QUERY,
      { organizationId, onDate },
    )
    add(data.meetingSessions)
  } catch {
    // Meetings unavailable / not permitted — the office is unaffected.
  }

  // Anything LIVE now, even if the date filter missed it.
  try {
    const data = await client.request<{ meetingSessions?: SessionNode[] | null }>(
      ACTIVE_SESSIONS_QUERY,
      { organizationId },
    )
    add(data.meetingSessions)
  } catch {
    // No live sessions / not permitted — fine.
  }

  return meetings
}
